/**
 * GlobalSearch - Command Palette Style Universal Search
 *
 * ⌘F / Ctrl+F to open anywhere in the platform (⌘K still works).
 * Searches across Jobs, Quotes, Files, Emails, Contacts, QA Records.
 *
 * @path packages/ui/src/components/GlobalSearch.tsx
 */

import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  Loader2,
  Command,
  Filter,
} from 'lucide-react'
import { cn, formatModFShortcut } from '../lib/utils'

// Sub-modules
import { useMemo } from 'react'
import { useSearch, useGlobalSearch } from './global-search/useSearch'
import { SearchEmptyState } from './global-search/SearchEmptyState'
import { SearchResults } from './global-search/SearchResults'
import { SearchFacetRail } from './global-search/SearchFacetRail'
import { HubsBar } from './global-search/HubsBar'
import {
  defaultQuickActions,
  defaultQuickNav,
  defaultQuickFilters,
  filterOptions as defaultFilterOptions,
} from './global-search/types'

// Re-export types and hook for public API
export type {
  GlobalSearchProps,
  SearchResult,
  SearchResultType,
  SearchProvider,
  QuickAction,
  LocalJob,
  QuickNavItem,
  QuickFilter,
} from './global-search/types'
export { useGlobalSearch }

import type { GlobalSearchProps } from './global-search/types'

export function GlobalSearch({
  isOpen,
  onClose,
  onSearch,
  apiClient,
  localJobs = [],
  quickActions = defaultQuickActions,
  quickNavItems = defaultQuickNav,
  quickFilters = defaultQuickFilters,
  recentJobs = [],
  searchProviders = [],
  initialActiveFilters,
  initialQuery,
  placeholder,
  pinnedHubs,
  onPinnedHubClick,
  currentHubId,
  panelMaxWidthClassName = 'max-w-3xl',
}: GlobalSearchProps) {
  const {
    query,
    setQuery,
    results,
    totalResults,
    facets,
    activeFacet,
    setActiveFacet,
    isLoading,
    selectedIndex,
    showFilters,
    setShowFilters,
    activeFilters,
    recentSearches,
    semanticAvailable,
    searchError,
    inputRef,
    listRef,
    toggleFilter,
    recordRecentSearch,
  } = useSearch({
    isOpen,
    onClose,
    onSearch,
    apiClient,
    localJobs,
    quickActions,
    searchProviders,
    initialActiveFilters,
    initialQuery,
  })

  // Merge built-in filter chips with provider-supplied chips.
  // Each provider that ships a label/icon contributes a chip the
  // user can toggle; built-in chips win on id collisions so a
  // module redefining e.g. "email" doesn't overwrite the platform
  // chip's icon.
  const filterOptions = useMemo(() => {
    const seen = new Set(defaultFilterOptions.map((f) => f.id))
    const extras = searchProviders
      .filter((p) => !!p.label && !seen.has(p.id))
      .map((p) => ({ id: p.id, label: p.label, icon: p.icon }))
    return [...defaultFilterOptions, ...extras]
  }, [searchProviders])

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center pt-0 sm:pt-[10vh]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        {/* Modal — theme tokens, not slate-*: slate-900 is a blue-hued gray
            that tinted the whole palette against the app's neutral surfaces.
            Phones get a full-screen sheet (flex column, safe-area padded);
            sm+ keeps the centred floating panel. */}
        <motion.div
          className={cn(
            'relative flex h-[100dvh] w-full flex-col bg-card shadow-2xl border border-border overflow-hidden',
            'pb-[env(safe-area-inset-bottom)] sm:pb-0',
            'rounded-none sm:mx-4 sm:h-auto sm:rounded-2xl',
            panelMaxWidthClassName,
          )}
          initial={{ y: -20, scale: 0.95, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: -20, scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder ?? 'Search jobs, quotes, files, emails...'}
              className="flex-1 bg-transparent text-lg font-medium text-foreground outline-none placeholder:text-muted-foreground"
              autoComplete="off"
              spellCheck={false}
            />
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
            ) : query ? (
              <button onClick={() => setQuery('')} className="p-1 rounded hover:bg-muted">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            ) : null}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                // `relative` anchors the count badge to THIS button.
                // Without it the badge's `absolute` positioning escaped
                // to the modal (the nearest positioned ancestor) and was
                // clipped by the modal's `overflow-hidden` into a stray
                // blue dot in the top-right corner.
                'relative shrink-0 p-1.5 rounded-lg transition-colors',
                showFilters || activeFilters.length > 0
                  ? 'bg-primary/15 text-primary'
                  : 'hover:bg-muted text-muted-foreground'
              )}
            >
              <Filter className="w-4 h-4" />
              {activeFilters.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 text-[10px] bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                  {activeFilters.length}
                </span>
              )}
            </button>
          </div>

          {/* Filters Row */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                className="border-b border-border bg-muted/50 overflow-hidden"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                {/* Horizontally scrollable chip rail. The chip set can
                    exceed the modal width (Jobs … QA + provider chips),
                    so the row scrolls instead of clipping the trailing
                    chips. Each chip is `shrink-0` so it never squashes. */}
                <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto [scrollbar-width:thin]">
                  <span className="text-xs text-muted-foreground mr-1 shrink-0">Filter:</span>
                  {filterOptions.map((filter) => {
                    // Provider-supplied chips may omit the icon; in that
                    // case we render the label by itself rather than
                    // crashing on `<undefined />`.
                    const Icon = filter.icon
                    const isActive = activeFilters.includes(filter.id)
                    return (
                      <button
                        key={filter.id}
                        onClick={() => toggleFilter(filter.id)}
                        className={cn(
                          'flex shrink-0 items-center gap-1.5 px-2 py-1 text-xs rounded-full whitespace-nowrap transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/70'
                        )}
                      >
                        {Icon ? <Icon className="w-3 h-3 shrink-0" /> : null}
                        {filter.label}
                      </button>
                    )
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results / Quick Actions — two-pane: centre = direct
              information, right rail = source "Pages" as icons. The
              rail only appears once the multi-source fan-out has
              produced ≥2 distinct sources for the current query. */}
          <div className="flex min-h-0 flex-1 sm:flex-none sm:max-h-[60vh]">
            <div ref={listRef} className="flex-1 min-w-0 overflow-y-auto">
              {!query.trim() && (
                <SearchEmptyState
                  quickNavItems={quickNavItems}
                  quickFilters={quickFilters}
                  quickActions={quickActions}
                  recentJobs={recentJobs}
                  recentSearches={recentSearches}
                  onClose={onClose}
                  onSetQuery={setQuery}
                />
              )}

              <SearchResults
                results={results}
                selectedIndex={selectedIndex}
                semanticAvailable={semanticAvailable}
                searchError={searchError}
                query={query}
                isLoading={isLoading}
                recentSearches={recentSearches}
                onSelectRecentSearch={setQuery}
                onRecordRecentSearch={recordRecentSearch}
                onClose={onClose}
              />
            </div>

            {query.trim() && facets.length > 1 && (
              <SearchFacetRail
                facets={facets}
                activeFacet={activeFacet}
                totalCount={totalResults}
                onSelect={setActiveFacet}
              />
            )}
          </div>

          {/* Hubs launcher — always visible (even while typing), pinned
              above the keyboard-hints footer. Populated by the platform
              shell; absent → renders nothing (other hosts unchanged). */}
          {pinnedHubs && pinnedHubs.length > 0 && (
            <HubsBar
              hubs={pinnedHubs}
              onHubClick={onPinnedHubClick}
              onClose={onClose}
              currentHubId={currentHubId}
            />
          )}

          {/* Footer — keyboard hints are meaningless on touch; hide on phones */}
          <div className="hidden items-center justify-between px-4 py-2 border-t border-border bg-muted/50 text-xs text-muted-foreground sm:flex">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded border border-border">
                  <ArrowUp className="w-3 h-3" />
                </kbd>
                <kbd className="px-1.5 py-0.5 bg-background rounded border border-border">
                  <ArrowDown className="w-3 h-3" />
                </kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded border border-border">
                  <CornerDownLeft className="w-3 h-3" />
                </kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded border border-border font-mono text-[10px]">
                  esc
                </kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="flex items-center gap-1 font-mono text-[10px]">
              <span>{formatModFShortcut()}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default GlobalSearch
